import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  TextField,
  IconButton,
  Paper,
  CircularProgress,
  Grid,
  Link
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * ReleaseNotesEditor component for editing release notes
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to call when dialog is closed
 * @param {string} props.fixVersion - Fix version name
 * @param {Array} props.items - Array of work items with summaries
 * @param {function} props.onApprove - Function to call when release notes are approved
 * @param {boolean} props.loading - Whether the component is loading
 * @returns {JSX.Element} ReleaseNotesEditor component
 */
const ReleaseNotesEditor = ({ 
  open, 
  onClose, 
  fixVersion, 
  items, 
  onApprove,
  loading = false
}) => {
  // State for editable items
  const [editableItems, setEditableItems] = useState(items || []);
  
  // Group items by type
  const features = editableItems.filter(item => 
    item.workItemType !== 'Bug'
  );
  
  // Use the bugSource field to categorize bugs
  const productionBugs = editableItems.filter(item => {
    // Check if the item is a bug
    if (item.workItemType === 'Bug') {
      // If bugSource is set, use it for categorization
      if (item.bugSource) {
        return item.bugSource === 'production';
      }
      
      // Fallback to issueSource if bugSource is not available
      if (item.issueSource) {
        // Convert issueSource to lowercase for case-insensitive comparison
        const issueSourceLower = typeof item.issueSource === 'string' ? item.issueSource.toLowerCase() : '';
        return issueSourceLower === 'production';
      }
    }
    return false;
  });
  
  const internalBugs = editableItems.filter(item => {
    // Check if the item is a bug
    if (item.workItemType === 'Bug') {
      // If bugSource is set, use it for categorization
      if (item.bugSource) {
        return item.bugSource === 'internal';
      }
      
      // Fallback to issueSource if bugSource is not available
      // If issueSource is missing or null, consider it an internal bug
      if (!item.issueSource) {
        return true;
      }
      // Convert issueSource to lowercase for case-insensitive comparison
      const issueSourceLower = typeof item.issueSource === 'string' ? item.issueSource.toLowerCase() : '';
      return issueSourceLower !== 'production';
    }
    return false;
  });
  
  console.log('Editable items count:', editableItems.length);
  console.log('Features count:', features.length);
  console.log('Production bugs count:', productionBugs.length);
  console.log('Internal bugs count:', internalBugs.length);
  
  // Handle title change
  const handleTitleChange = (id, newTitle) => {
    setEditableItems(prevItems => 
      prevItems.map(item => 
        item.id === id ? { ...item, title: newTitle } : item
      )
    );
  };
  
  // Handle item deletion
  const handleDeleteItem = (id) => {
    setEditableItems(prevItems => 
      prevItems.filter(item => item.id !== id)
    );
  };
  
  // Handle approve
  const handleApprove = () => {
    // Send items to backend
    onApprove(editableItems);
  };
  
  // Get work item type display value
  const getWorkItemType = (item) => {
    if (item.level) return item.level; // Epic, Feature, PBI
    if (item.workItemType) return item.workItemType;
    return 'Unknown';
  };

  // Reset items when dialog opens with new items
  React.useEffect(() => {
    if (open && items) {
      setEditableItems(items);
    }
  }, [open, items]);
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh', width: '90%' }
      }}
    >
      <DialogTitle>
        Edit Release Notes for {fixVersion}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Features Section */}
            {features.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom>Feature Changes</Typography>
                {features.map(item => (
                  <Box key={item.id} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, borderLeft: '5px solid #2ecc71' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={2}>
                          <Typography variant="body2" color="textSecondary">ADO ID</Typography>
                          <Link 
                            href={`https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${item.id}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            sx={{ color: '#1976d2', textDecoration: 'none', fontWeight: 'bold' }}
                          >
                            {item.id}
                          </Link>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Typography variant="body2" color="textSecondary">Work Item Type</Typography>
                          <Typography variant="body1">{getWorkItemType(item)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            fullWidth
                            label="Title"
                            value={item.title || ''}
                            onChange={(e) => handleTitleChange(item.id, e.target.value)}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                      </Grid>
                      <IconButton onClick={() => handleDeleteItem(item.id)} size="small" sx={{ ml: 2 }}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
                <Divider sx={{ my: 2 }} />
              </>
            )}
            
            {/* Production Bugs Section */}
            <Typography variant="h6" gutterBottom>Customer Defects</Typography>
            {productionBugs.length > 0 ? (
              productionBugs.map(item => (
                <Box key={item.id} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, borderLeft: '5px solid #e74c3c' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={2}>
                        <Typography variant="body2" color="textSecondary">ADO ID</Typography>
                        <Link 
                          href={`https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${item.id}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ color: '#1976d2', textDecoration: 'none', fontWeight: 'bold' }}
                        >
                          {item.id}
                        </Link>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Typography variant="body2" color="textSecondary">Work Item Type</Typography>
                        <Typography variant="body1">{getWorkItemType(item)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          label="Title"
                          value={item.customer ? `[${item.customer}] ${item.title}` : item.title || ''}
                          onChange={(e) => handleTitleChange(item.id, e.target.value)}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                    </Grid>
                    <IconButton onClick={() => handleDeleteItem(item.id)} size="small" sx={{ ml: 2 }}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))
            ) : (
              <Paper elevation={1} sx={{ p: 2, mb: 2, color: '#999', fontStyle: 'italic' }}>
                No production bugs in this release.
              </Paper>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            {/* Internal Bugs Section */}
            <Typography variant="h6" gutterBottom>Internal Bugs</Typography>
            {internalBugs.length > 0 ? (
              internalBugs.map(item => (
                <Box key={item.id} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, borderLeft: '5px solid #f39c12' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={2}>
                        <Typography variant="body2" color="textSecondary">ADO ID</Typography>
                        <Link 
                          href={`https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${item.id}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ color: '#1976d2', textDecoration: 'none', fontWeight: 'bold' }}
                        >
                          {item.id}
                        </Link>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Typography variant="body2" color="textSecondary">Work Item Type</Typography>
                        <Typography variant="body1">{getWorkItemType(item)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          label="Title"
                          value={item.title || ''}
                          onChange={(e) => handleTitleChange(item.id, e.target.value)}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                    </Grid>
                    <IconButton onClick={() => handleDeleteItem(item.id)} size="small" sx={{ ml: 2 }}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))
            ) : (
              <Paper elevation={1} sx={{ p: 2, mb: 2, color: '#999', fontStyle: 'italic' }}>
                No internal bugs in this release.
              </Paper>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button 
          onClick={handleApprove} 
          variant="contained" 
          color="primary"
          disabled={loading}
        >
          {loading ? 'Processing...' : 'Approve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReleaseNotesEditor;
